import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import './BoxingStation.css';

function BoxingStation({ salesOrder, scannedItems, onComplete, onBack }) {
  const [selectedBox, setSelectedBox] = useState(null);
  const [weight, setWeight] = useState('');
  const [isCapturing, setIsCapturing] = useState(false);
  const [manualEntry, setManualEntry] = useState(false);
  const [boxes, setBoxes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('Select a box size');

  // Box types from database
  const [boxTypes, setBoxTypes] = useState([]);

  // Fetch box types on mount
  useEffect(() => {
    fetchBoxTypes();
  }, []);

  const fetchBoxTypes = async () => {
    try {
      const response = await axios.get('/api/settings/shipping-boxes/active');
      setBoxTypes(response.data.map(box => ({
        id: box.id,
        name: box.name,
        dimensions: `${box.length}x${box.width}x${box.height}`,
        icon: box.icon || '📦',
        maxWeight: box.max_weight
      })));
    } catch (error) {
      console.error('Error loading box types:', error);
      // Fallback to default boxes if API fails
      setBoxTypes([
        { id: 'small', name: 'Small Box', dimensions: '8x6x4', icon: '📦' },
        { id: 'medium', name: 'Medium Box', dimensions: '12x10x8', icon: '📦' },
        { id: 'large', name: 'Large Box', dimensions: '16x14x12', icon: '📦' },
        { id: 'xl', name: 'XL Box', dimensions: '20x18x16', icon: '📦' }
      ]);
    }
  };

  // Start capturing weight from serial scale
  const startWeightCapture = async () => {
    if (!selectedBox) {
      setMessage('Please select a box first');
      return;
    }

    setIsCapturing(true);
    setMessage('Place box on scale...');

    try {
      // Get weight from scale API
      const response = await axios.get('/api/scale/weight');

      if (response.data && response.data.weight) {
        setWeight(response.data.weight.toFixed(2));
        setIsCapturing(false);
        setMessage(`Weight captured: ${response.data.weight.toFixed(2)} lbs${response.data.simulated ? ' (simulated)' : ''}`);
      } else {
        throw new Error('Invalid weight response');
      }
    } catch (error) {
      console.error('Scale error:', error);
      setIsCapturing(false);
      setMessage('Failed to capture weight. Please try again.');
    }
  };

  // Add box to shipment
  const handleAddBox = () => {
    if (!selectedBox || !weight) {
      setMessage('Please select box and capture weight');
      return;
    }

    const newBox = {
      id: boxes.length + 1,
      type: selectedBox.id,
      name: selectedBox.name,
      dimensions: selectedBox.dimensions,
      weight: parseFloat(weight),
      itemCount: Math.ceil(scannedItems.length / (boxes.length + 1))
    };

    setBoxes([...boxes, newBox]);
    setSelectedBox(null);
    setWeight('');
    setMessage('Box added. Select another or complete shipment.');
  };

  // Complete fulfillment and create in NetSuite
  const handleComplete = async () => {
    if (boxes.length === 0) {
      setMessage('Please add at least one box');
      return;
    }

    setLoading(true);
    try {
      // Prepare fulfillment data for NetSuite
      // NetSuite returns fields in lowercase, so check both formats
      const fulfillmentData = {
        salesOrderId: salesOrder.id,
        orderNumber: salesOrder.orderNumber,
        customerId: salesOrder.customerId,
        items: scannedItems.reduce((acc, item) => {
          const id = item.itemid || item.itemId;  // Handle both lowercase and camelCase
          const existing = acc.find(i => i.itemId === id);
          if (existing) {
            existing.quantity++;
          } else {
            acc.push({
              itemId: id,
              sku: item.sku,
              quantity: 1
            });
          }
          return acc;
        }, []),
        packages: boxes.map(box => ({
          type: box.type,
          dimensions: box.dimensions,
          weight: box.weight,
          itemCount: box.itemCount
        }))
      };

      // Create Item Fulfillment in NetSuite
      const response = await axios.post('/api/fulfillment/create', fulfillmentData);

      if (response.data.success) {
        setMessage(`Fulfillment ${response.data.fulfillmentId} created in NetSuite!`);

        // Pass fulfillment data and boxes to the next step
        onComplete({
          ...response.data,
          boxes: boxes,
          showDocuments: true
        });
      } else {
        throw new Error(response.data.error || 'Failed to create fulfillment');
      }
    } catch (error) {
      setMessage(`Error: ${error.response?.data?.error || error.message || 'Failed to create fulfillment'}`);
      console.error('Fulfillment error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="boxing-station">
      <div className="boxing-header">
        <button className="back-button" onClick={onBack}>
          ← Back to Scanning
        </button>
        <h2>Boxing Station - Order #{salesOrder.orderNumber}</h2>
      </div>

      <div className="message-bar">
        {message}
      </div>

      <div className="boxing-content">
        {/* Box Selection Grid */}
        <div className="box-selection">
          <h3>Select Box Size</h3>
          <div className="box-grid">
            {boxTypes.map(box => (
              <motion.div
                key={box.id}
                className={`box-option ${selectedBox?.id === box.id ? 'selected' : ''}`}
                onClick={() => setSelectedBox(box)}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <div className="box-icon">{box.icon}</div>
                <div className="box-name">{box.name}</div>
                <div className="box-dims">{box.dimensions}</div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Weight Capture */}
        <div className="weight-section">
          <h3>Weight Capture</h3>
          {selectedBox && (
            <div className="selected-box-info">
              Selected: {selectedBox.name} ({selectedBox.dimensions})
            </div>
          )}

          <div className="weight-controls">
            {!manualEntry ? (
              <>
                <button
                  className={`capture-button ${isCapturing ? 'capturing' : ''}`}
                  onClick={startWeightCapture}
                  disabled={!selectedBox || isCapturing}
                >
                  {isCapturing ? 'Reading Scale...' : 'Capture Weight'}
                </button>

                <button
                  className="manual-entry-button"
                  onClick={() => setManualEntry(true)}
                  disabled={!selectedBox}
                >
                  Manual Entry
                </button>
              </>
            ) : (
              <div className="manual-weight-entry">
                <input
                  type="number"
                  step="0.01"
                  placeholder="Enter weight"
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                  className="weight-input"
                  autoFocus
                />
                <span className="weight-unit">lbs</span>
                <button
                  className="confirm-weight-button"
                  onClick={() => {
                    if (weight && parseFloat(weight) > 0) {
                      setManualEntry(false);
                      setMessage(`Weight entered: ${weight} lbs`);
                    }
                  }}
                  disabled={!weight || parseFloat(weight) <= 0}
                >
                  ✓
                </button>
                <button
                  className="cancel-weight-button"
                  onClick={() => {
                    setManualEntry(false);
                    setWeight('');
                  }}
                >
                  ✕
                </button>
              </div>
            )}

            {weight && !manualEntry && (
              <div className="weight-display">
                <span className="weight-value">{weight}</span>
                <span className="weight-unit">lbs</span>
                <button
                  className="edit-weight-button"
                  onClick={() => setManualEntry(true)}
                  title="Edit weight"
                >
                  ✏️
                </button>
              </div>
            )}
          </div>

          {weight && (
            <button className="add-box-button" onClick={handleAddBox}>
              Add Box to Shipment
            </button>
          )}
        </div>

        {/* Current Boxes */}
        {boxes.length > 0 && (
          <div className="current-boxes">
            <h3>Shipment Boxes</h3>
            <div className="boxes-list">
              {boxes.map(box => (
                <div key={box.id} className="box-item">
                  <span className="box-number">Box {box.id}</span>
                  <span className="box-details">
                    {box.name} • {box.weight} lbs
                  </span>
                </div>
              ))}
            </div>

            <div className="shipment-summary">
              <div>Total Boxes: {boxes.length}</div>
              <div>Total Weight: {boxes.reduce((sum, b) => sum + b.weight, 0).toFixed(2)} lbs</div>
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons */}
      <div className="boxing-actions">
        <button
          className="add-another-button"
          onClick={() => {
            setSelectedBox(null);
            setWeight('');
            setMessage('Select another box size');
          }}
          disabled={!weight || loading}
        >
          Add Another Box
        </button>

        <button
          className="complete-button"
          onClick={handleComplete}
          disabled={boxes.length === 0 || loading}
        >
          {loading ? 'Processing...' : `Complete Shipment (${boxes.length} box${boxes.length !== 1 ? 'es' : ''})`}
        </button>
      </div>
    </div>
  );
}

export default BoxingStation;